import SEO from '../components/SEO';
import Schema, { createProductSchema } from '../components/Schema';
import React from 'react';
import { motion } from 'framer-motion';
import { TruckIcon, CheckCircle2, MapPin, Clock, Bell, Camera, Package, BarChart3, Users, Smartphone, Route, Target, Zap, Shield, Check, ArrowRight, RefreshCw, Factory } from 'lucide-react';
import ProductCTA from '../components/ProductCTA';

const StreamPage = () => {
  const stats = [
    { value: "2-Way", label: "SAP B1 Integration" },
    { value: "Real-time", label: "Order Sync" },
    { value: "Live", label: "Delivery Tracking" },
    { value: "Digital", label: "Proof of Delivery" }
  ];

  const benefits = [
    { 
      icon: Package, 
      title: "Automatic Order Creation",
      strapline: "No manual data entry",
      desc: "Push SAP Business One sales orders to Stream automatically. New orders ready for delivery planning instantly."
    },
    { 
      icon: RefreshCw, 
      title: "Instant Order Updates",
      strapline: "Perfect synchronisation",
      desc: "Changes in SAP B1 instantly reflected in Stream. Your delivery teams always work with current information."
    },
    { 
      icon: Bell, 
      title: "Delivery Tracking & Notifications",
      strapline: "Customer communication",
      desc: "Real-time tracking with automated customer notifications. Tracking URLs made available in SAP Business One."
    },
    { 
      icon: Camera, 
      title: "Digital Proof of Delivery",
      strapline: "Photos and signatures",
      desc: "Capture POD in Stream mobile app. Photos, signatures, and delivery notes automatically sync back to SAP B1."
    },
    { 
      icon: BarChart3, 
      title: "Inventory Auto-Updates",
      strapline: "Perfect stock accuracy",
      desc: "Delivered quantities update SAP B1 stock levels in real-time. No manual stock adjustments needed."
    },
    { 
      icon: Route, 
      title: "Smart Route Optimisation",
      strapline: "Lower costs, faster delivery",
      desc: "AI-powered route planning reduces fuel costs and improves delivery times. More deliveries per day."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Stream Transport Management | Delivery & Logistics for SAP Business One"
        description="Transport management system for SAP B1. Route optimization, ePOD, real-time tracking, and automated delivery management for efficient logistics."
        keywords="Stream TMS, transport management, SAP B1 delivery, logistics software, ePOD solution"
        canonical="https://www.ascarii.com/products/stream"
      />
      
      {/* Hero */}
      <section className="relative min-h-[600px] bg-gradient-to-br from-teal-700 via-teal-800 to-cyan-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block px-4 py-2 bg-teal-500/20 backdrop-blur-sm border border-teal-400/30 rounded-full text-sm font-semibold mb-6">Transport Management for SAP B1</div>
              
              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 rounded-xl bg-teal-500 flex items-center justify-center">
                  <TruckIcon className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">Stream</h1>
              </div>
              
              <p className="text-xl md:text-2xl text-teal-100 mb-4 font-medium">From SAP order to customer doorstep</p>
              <p className="text-lg text-teal-50 mb-8 leading-relaxed">Seamless two-way integration between SAP Business One and Stream Transport Management. Plan, execute, and track deliveries with real-time visibility and automated proof of delivery.</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img src="/stream-images/stream-hero-new.png" alt="Stream TMS" className="w-full h-[400px] object-contain bg-white" loading="eager" />
              </div>
            </motion.div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0"><svg viewBox="0 0 1440 120" fill="none" className="w-full h-16"><path d="M0 0L60 10C120 20 240 40 360 46.7C480 53 600 47 720 43.3C840 40 960 40 1080 46.7C1200 53 1320 67 1380 73.3L1440 80V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V0Z" fill="white"/></svg></div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.1 }} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-teal-600 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Stream - Dark Teal Theme */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-teal-700 via-teal-800 to-cyan-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why Connect SAP Business One with Stream?</h2>
            <p className="text-xl text-blue-100 max-w-4xl mx-auto">Two-way integration for seamless delivery operations</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.05 }} viewport={{ once: true }} className="group p-8 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 hover:border-cyan-300 hover:bg-white/20 transition-all">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                    <p className="text-sm text-cyan-300 font-medium mb-2">{benefit.strapline}</p>
                    <p className="text-blue-100 text-sm leading-relaxed">{benefit.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Flow - Alternating Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="relative group flex">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full max-h-[450px]">
                <img src="https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?w=800&q=85" alt="Delivery Planning" className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tr from-teal-600/20 to-transparent" />
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="flex flex-col justify-center">
              <div className="inline-block px-4 py-2 bg-teal-500/20 text-teal-600 rounded-full text-sm font-semibold mb-4 w-fit">Seamless Integration</div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">From Order to Doorstep</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">Stream connects directly to SAP Business One for complete visibility from sales order creation to final delivery and proof of delivery capture.</p>
              <div className="space-y-3">
                {["Sales orders automatically create delivery jobs", "Real-time synchronisation between SAP B1 and Stream", "Driver app with mobile proof of delivery", "Customer tracking portal with live updates", "Automated delivery status updates in SAP B1", "Stock levels update on successful delivery"].map((item, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-sm">{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Perfect For - Industry Cards with Icons Beside Text */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Perfect for SAP Business One Users</h2>
            <p className="text-xl text-gray-600">Businesses managing their own delivery fleets</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Users, title: "Wholesale Distributors", strapline: "Multi-drop efficiency", points: ["Multi-drop delivery routes", "Customer time windows", "Vehicle capacity planning", "Driver performance tracking"] },
              { icon: Factory, title: "Manufacturing with Delivery", strapline: "Production to delivery", points: ["Production to delivery visibility", "Just-in-time delivery scheduling", "Installation appointment management", "Returns and collections"] },
              { icon: Package, title: "Building Products", strapline: "Site delivery management", points: ["Site delivery management", "Delivery slot booking", "Two-man delivery coordination", "POD with damage reporting"] },
              { icon: Shield, title: "Industrial Suppliers", strapline: "Compliance ready", points: ["Bulk delivery management", "Dangerous goods compliance", "Multi-site depot coordination", "Customer portal access"] }
            ].map((sector, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.05 }} viewport={{ once: true }} className="group bg-gradient-to-br from-slate-50 to-white rounded-xl p-8 border border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500/10 to-sky-500/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <sector.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{sector.title}</h3>
                    <p className="text-sm text-blue-600 font-medium mb-4">{sector.strapline}</p>
                    <ul className="space-y-2">
                      {sector.points.map((point, i) => (
                        <li key={i} className="flex items-start space-x-2">
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-600 text-sm">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-gray-100 via-blue-50 to-gray-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="lg:col-span-2">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Transform Your Delivery Operations</h2>
              <p className="text-xl text-gray-600 mb-8">Seamless SAP Business One integration</p>
              <div className="space-y-6">
                {[
                  { icon: Zap, title: "Quick Implementation", desc: "Integrate with SAP B1 in days" },
                  { icon: Shield, title: "Proven Platform", desc: "Trusted by UK distributors" },
                  { icon: Target, title: "Complete Solution", desc: "Planning to proof of delivery" }
                ].map((item, i) => (
                  <div key={i} className="flex items-center space-x-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/10 to-sky-500/10 flex items-center justify-center">
                      <item.icon className="w-7 h-7 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">{item.title}</h4>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="lg:col-span-3">
              <ProductCTA 
                productName="Stream for SAP Business One" 
                brandColor="blue-600"
                headline="Ready to Optimise Your Deliveries?"
                description="Let's discuss how Stream integrates with your SAP Business One system to transform delivery operations."
                benefits={[
                  "Two-way SAP B1 integration",
                  "Real-time delivery tracking",
                  "Digital proof of delivery",
                  "Route optimisation"
                ]}
              />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StreamPage;
